import React, { useState, useEffect, Suspense, lazy } from "react"; // Suspense ve lazy eklendi
import { Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Sidebar from "./components/Sidebar";

// Sayfa bileşenleri React.lazy ile dinamik olarak import ediliyor
const Home = lazy(() => import("./pages/Home"));
const Stats = lazy(() => import("./pages/Stats"));
const Stream = lazy(() => import("./pages/Stream"));
const Crosshairs = lazy(() => import("./pages/Crosshairs"));
const CrosshairGenerator = lazy(() => import("./pages/CrosshairGenerator"));
const Socials = lazy(() => import("./pages/Socials"));

// Tema renk paletleri
const COLORS = {
  background: "#F4F8FB",
  cyan: "#00B8D4",
  blue: "#4C8BF5",
  dark: "#212121",
  text: "#2D2D2D",
  soft: "#F1F8FF",
};

const darkPalette = {
  background: "#212121",
  cyan: "#80DEEA",
  blue: "#64B5F6",
  dark: "#121212",
  text: "#E0E0E0",
  soft: "#2C3E50",
};

// Animasyon variantları
const variants = {
  initial: { opacity: 0, y: 32, scale: 0.97 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -24, scale: 0.96 },
};

// Route tipleri
const pageContainerTypes = {
  "/": "full",
  "/stats": "full",
  "/stream": "full",
  "/crosshairs": "full",
  "/crosshairs/generator": "full",
  "/socials": "full",
  "/player-search": "full",
};

// Animasyonlu route wrapper
function AnimatedRoutes({ themeStyles }) {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route
          path="/"
          element={
            <motion.div variants={variants} initial="initial" animate="animate" exit="exit">
              <Home themeStyles={themeStyles} />
            </motion.div>
          }
        />
        <Route
          path="/stats"
          element={
            <motion.div variants={variants} initial="initial" animate="animate" exit="exit">
              <Stats themeStyles={themeStyles} />
            </motion.div>
          }
        />
        <Route
          path="/stream"
          element={
            <motion.div variants={variants} initial="initial" animate="animate" exit="exit">
              <Stream themeStyles={themeStyles} />
            </motion.div>
          }
        />
        <Route
          path="/crosshairs"
          element={
            <motion.div variants={variants} initial="initial" animate="animate" exit="exit">
              <Crosshairs themeStyles={themeStyles} />
            </motion.div>
          }
        />
        <Route
          path="/crosshairs/generator"
          element={
            <motion.div variants={variants} initial="initial" animate="animate" exit="exit">
              <CrosshairGenerator themeStyles={themeStyles} />
            </motion.div>
          }
        />
        <Route
          path="/socials"
          element={
            <motion.div variants={variants} initial="initial" animate="animate" exit="exit">
              <Socials themeStyles={themeStyles} />
            </motion.div>
          }
        />
      </Routes>
    </AnimatePresence>
  );
}

// Asıl App component'i
export default function App() {
  // Sadece karanlık mod: darkMode her zaman true
  const darkMode = true;
  // setDarkMode ve localStorage ile ilgili kısımlar kaldırıldı

  // Yayın durumu state'i ve popup gösterimi
  const [isLive, setIsLive] = useState(false);
  const [showLivePopup, setShowLivePopup] = useState(false);


  // Yayın durumunu periyodik olarak kontrol et
  useEffect(() => {
    const fetchStreamStatus = async () => {
      try {
        // Admin paneli public_html altında olduğu için /public_html/admin/stream_status_manager.php
        // Ancak, bu dosya admin girişi gerektiriyor.
        // Herkesin erişebileceği ayrı bir PHP dosyası oluşturmak daha iyi olabilir
        // veya stream_status.json dosyasını doğrudan okunabilir bir yere koymak.
        // Şimdilik, stream_status.json dosyasının public kök dizininde olduğunu varsayalım.
        // Bu dosya admin panelinden güncellenecek.
        // stream_status.json dosyası public/public_html/admin/ klasöründe olduğu için yol güncellendi.
        const response = await fetch('/admin/stream_status.json?t=' + new Date().getTime()); // Cache-busting için timestamp eklendi
        if (response.ok) {
          const data = await response.json();
          // Sadece durum değiştiyse popup'ı göster/gizle ve state'i güncelle
          if (data.isLive !== isLive) {
            setIsLive(data.isLive);
            if (data.isLive) {
              setShowLivePopup(true);
            } else {
              setShowLivePopup(false); // Yayın kapandıysa popup'ı gizle
            }
          } else if (data.isLive && !showLivePopup) {
            // Bu koşul, yayın canlıyken ve popup manuel olarak kapatılmışsa popup'ın tekrar açılmasını engeller.
            // Ancak, yayın durumu kapalıdan canlıya geçerse popup yine de açılır.
          }
        } else {
          // console.warn('Yayın durumu alınamadı.');
          if (isLive) setIsLive(false); // Hata durumunda ve önceden canlıysa, kapalıya çek
          if (showLivePopup) setShowLivePopup(false); // Hata durumunda popup'ı gizle
        }
      } catch (error) {
        // console.error('Yayın durumu alınırken hata:', error);
        if (isLive) setIsLive(false);
        if (showLivePopup) setShowLivePopup(false);
      }
    };

    fetchStreamStatus(); // İlk yüklemede kontrol et
    const intervalId = setInterval(fetchStreamStatus, 30000); // Kontrol aralığı 30 saniyeye düşürüldü

    return () => clearInterval(intervalId); // Component unmount olduğunda interval'ı temizle
  }, [isLive, showLivePopup]); // showLivePopup da dependency array'e eklendi


  // İlk açılışta ziyaretçi sayısı (isteğe bağlı)
  useEffect(() => {
    fetch('/visitor.php')
      .then(res => res.text())
      .then(count => console.log("Toplam Ziyaret:", count));
  }, []);

  // Tema renklerini seç
  const palette = darkMode ? darkPalette : COLORS;
  const themeStyles = {
    background: palette.background,
    color: palette.text,
    cyan: palette.cyan,
    blue: palette.blue,
    soft: palette.soft,
  };

  const location = useLocation();
  const containerType = pageContainerTypes[location.pathname] || "full";

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        background: `linear-gradient(120deg, ${palette.background} 60%, ${palette.soft} 100%)`,
        color: palette.text,
        fontFamily: `'Poppins', 'Inter', 'Segoe UI', Arial, sans-serif`,
        transition: "background 0.4s, color 0.4s",
      }}
    >
      {/* Sidebar'dan setDarkMode prop'u kaldırıldı, isLive prop'u eklendi */}
      <Sidebar darkMode={darkMode} palette={palette} isLive={isLive} />

      <main
        style={{
          flex: 1,
          marginLeft: 96, // Sidebar genişliği
          padding: "36px 0", // Üst ve alt padding
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: location.pathname === "/crosshairs/generator" ? "stretch" : "center", // Main için de koşullu alignItems
          background: "none",
        }}
      >
        {containerType === "full" ? (
          <div
            style={{
              width: "100%",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              // justifyContent ve alignItems şimdi koşullu olacak
              justifyContent: location.pathname === "/crosshairs/generator" ? "flex-start" : "center",
              alignItems: location.pathname === "/crosshairs/generator" ? "stretch" : "center",
              minHeight: "80vh",
              padding: 0,
              background: "none",
              boxShadow: "none",
              borderRadius: 0,
            }}
          >
            <Suspense fallback={<div style={{ color: themeStyles.color, fontSize: '20px', textAlign: 'center', marginTop: '50px' }}>Yükleniyor...</div>}>
              <AnimatedRoutes themeStyles={themeStyles} />
            </Suspense>
          </div>
        ) : (
          <div
            style={{
              borderRadius: 24,
              boxShadow: `0 8px 36px -10px ${palette.blue}24`,
              background: darkMode
                ? `linear-gradient(120deg, ${palette.dark} 60%, ${palette.soft} 100%)`
                : `linear-gradient(120deg, #fff 80%, ${palette.soft} 100%)`,
              padding: "32px 24px 28px 24px",
              width: "100%",
              maxWidth: 1000,
              minHeight: 480,
              margin: "0 auto",
              transition: "background 0.4s",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Suspense fallback={<div style={{ color: themeStyles.color, fontSize: '20px', textAlign: 'center', marginTop: '50px' }}>Yükleniyor...</div>}>
              <AnimatedRoutes themeStyles={themeStyles} />
            </Suspense>
          </div>
        )}
      </main>

      {/* Canlı Yayın Popup */}
      {showLivePopup && isLive && (
        <div
          style={{
            position: 'fixed',
            top: '25px', // Slightly more padding from top
            right: '25px', // Slightly more padding from right
            backgroundColor: '#4CAF50', // Green background
            color: 'white',
            padding: '20px 30px', // Increased padding
            borderRadius: '12px', // More rounded corners
            boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            gap: '20px', // Increased gap
            animation: 'slideInFromRightPopup 0.5s ease-out forwards, pulseBorder 2s infinite'
          }}
        >
          <style>
            {`
              @keyframes slideInFromRightPopup {
                from {
                  transform: translateX(100%);
                  opacity: 0;
                }
                to {
                  transform: translateX(0);
                  opacity: 1;
                }
              }
              @keyframes blinkLed {
                0%, 100% { background-color: #90EE90; box-shadow: 0 0 3px #90EE90; } /* Lighter green */
                50% { background-color: #32CD32; box-shadow: 0 0 8px #32CD32, 0 0 12px #32CD32; } /* Brighter green */
              }
              @keyframes pulseBorder {
                0% { box-shadow: 0 6px 20px rgba(0,0,0,0.25), 0 0 0 0 rgba(76, 175, 80, 0.7); }
                70% { box-shadow: 0 6px 20px rgba(0,0,0,0.25), 0 0 0 10px rgba(76, 175, 80, 0); }
                100% { box-shadow: 0 6px 20px rgba(0,0,0,0.25), 0 0 0 0 rgba(76, 175, 80, 0); }
              }
              .popup-close-btn {
                background: none;
                border: none;
                color: white;
                font-size: 24px; /* Larger close button */
                cursor: pointer;
                padding: 0 5px; /* Add some padding for easier click */
                line-height: 1;
                align-self: flex-start; /* Align to top */
              }
              .blinking-led {
                width: 12px;
                height: 12px;
                border-radius: 50%;
                animation: blinkLed 1.5s infinite;
                margin-right: 8px; /* Space between LED and text */
              }
            `}
          </style>
          <img src="images/kick_logo.png" alt="Kick Logo" style={{ width: '32px', height: '32px' }} /> {/* Using img tag for Kick logo */}
          <div className="blinking-led"></div> {/* Blinking LED element */}
          <div>
            <p style={{ margin: 0, fontWeight: 'bold', fontSize: '18px' }}> {/* Increased font size */}
              overdosevl şuanda Kick'te canlı yayında!
            </p>
            <a
              href="https://kick.com/overdosevl"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#E0E0E0', textDecoration: 'underline', fontWeight: '600', fontSize: '15px' }} // Adjusted link color for green bg, slightly larger
              onClick={() => setShowLivePopup(false)}
            >
              Buraya Tıkla ve yayına git
            </a>
          </div>
          <button className="popup-close-btn" onClick={() => setShowLivePopup(false)}>&times;</button>
        </div>
      )}
    </div>
  );
}
